import { useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal as BootstrapModal } from 'react-bootstrap'

import { formatDate, formatDateDDMMYYYY, getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { UserAvatar } from '../../../components/UserAvatar/UserAvatar'
import { Button } from '../../../components/Button/Button'
import { Modal } from '../../../components/Modal/Modal'

import { DocumentRecord } from '../../../models/Document'

import {
  selectDocumentBatchContact,
  selectDocumentBatchCreatedAt,
  selectDocumentBatchCreator,
  selectDocumentBatchDocuments,
} from '../../../store/processDocument/documentBatch'
import { useDeleteDocumentsByBatchMutation } from '../../../store/ai/documents'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { getInitials } from '../../utils'

import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'

import { DocumentRow } from '../Documents/DocumentRow'
import { useDocumentLeaveBypass } from '../DocumentLeaveBypassContext'
import { DocumentBatchHeader } from './DocumentBatchHeader'
import { useRefetchDocumentBatch } from './hooks'

import './DocumentBatch.scss'
import { User } from '../../../models/User'
import { useNavigate } from 'react-router-dom'

export const DocumentBatch = () => {
  const documents = useAppSelector(selectDocumentBatchDocuments)
  const createdAt = useAppSelector(selectDocumentBatchCreatedAt)
  const contact = useAppSelector(selectDocumentBatchContact)
  const creator = useAppSelector(selectDocumentBatchCreator)

  const { refetchDocumentBatch } = useRefetchDocumentBatch()

  const { portalRef } = useContext(HeaderContext)

  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)
  const [show, setShow] = useState<boolean>(false)

  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  if (!documents.length || !createdAt || !contact || !creator) {
    return null
  }

  const creatorUserDisplayName = getUserDisplayName(creator)

  const showDeleteModal = () => {
    setShow(true)
  }

  const hideDeleteModal = () => {
    setShow(false)
  }

  return (
    <>
      {headerContainer ? createPortal(<DocumentBatchHeader />, headerContainer) : null}
      {show ? <DeleteDocumentBatchModal show={show} onHide={hideDeleteModal} documents={documents} createdAt={createdAt} contact={contact} /> : null}
      <div className='d-flex flex-column zocks-gap-3'>
        <div className='d-flex align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-column gap-1'>
            <h2>
              {formatDateDDMMYYYY(createdAt)} - {getUserDisplayName(contact)}
            </h2>
            <div className='d-flex align-items-center zocks-gap-0.75 paragraph-medium zocks-text-gray-700'>
              <span>{formatDate(createdAt)}</span>
              <div className='document-batch-header-divider zocks-bg-color-gray-700 rounded-circle' />
              <div className='d-flex align-items-center gap-1'>
                <UserAvatar
                  width='1rem'
                  height='1rem'
                  className='document-batch-creator-initials fw-bolder zocks-bg-color-gray-700 zocks-text-white'
                  useInitials
                  initialLetters={getInitials(creatorUserDisplayName)}
                />
                <span className=''>{creatorUserDisplayName}</span>
              </div>
            </div>
          </div>
          <Button variant='header' onClick={showDeleteModal}>
            Delete all
          </Button>
        </div>
        <div className='d-flex flex-column gap-3'>
          {documents.map((document) => (
            <DocumentRow key={document.id} document={document} onDelete={refetchDocumentBatch} />
          ))}
        </div>
      </div>
    </>
  )
}

interface DeleteDocumentBatchModalProps {
  show: boolean
  onHide: () => void
  documents: DocumentRecord[]
  createdAt: string
  contact: User
}

const DeleteDocumentBatchModal: React.FC<DeleteDocumentBatchModalProps> = ({ show, onHide, documents, createdAt, contact }) => {
  const accountId = useAppSelector(selectAccountId)

  const navigate = useNavigate()

  const { bypassOnce } = useDocumentLeaveBypass()

  const [triggerDeleteDocumentsByBatch, { isLoading: deleteLoading }] = useDeleteDocumentsByBatchMutation()

  const documentBatchId = documents[0]?.documentBatchId

  const handleDelete = async () => {
    if (!documentBatchId) {
      return
    }

    try {
      await triggerDeleteDocumentsByBatch({ accountId, documentBatchId }).unwrap()

      bypassOnce()
      navigate('/documents/upload')
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={
        <BootstrapModal.Title>
          Delete {documents.length} {documents.length > 1 ? 'documents' : 'document'}
        </BootstrapModal.Title>
      }
      headerBodySeparator={true}
      bodyClassName='zocks-pb-0.75'
      body={
        <span>
          By deleting{' '}
          <strong>
            {formatDateDDMMYYYY(createdAt)} - {getUserDisplayName(contact)}
          </strong>{' '}
          package, all belonging files ({documents.length}) and their summaries will be deleted and all unsynchronized insights will be permanently lost.
        </span>
      }
      footerClassName='zocks-pt-0.75 zocks-px-1.5 zocks-pb-1.5'
      showFooterActions={true}
      onFooterPrimaryAction={handleDelete}
      footerPrimaryActionLoading={deleteLoading}
      footerPrimaryActionTitle='Delete'
      footerPrimaryActionButtonVariant='danger'
    />
  )
}
